import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  onMounted,
  inject,
  onBeforeUnmount,
  computed,
  onActivated,
} from "vue";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import {
  CustomCodeGroup,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowLoadingImage,
} from "@/types";
import SearchInput from "@/components/shared/inputs/SearchInput.vue";
import router from "@/router";
import VocRow from "@/components/bbs/voc/VocRow.vue";
import { useUserStore } from "@/stores/userStore";
import Modal from "@/components/shared/modal/base/Modal.vue";
import { useBbsStore } from "@/stores/bbsStore";
import { useGetVocList } from "@/composables/user/useGetVocList";
import { IVoc } from "@/domain/entities/bbs";
import { useServiceStore } from "@/stores/serviceStore";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";
import CheckboxInput from "@/components/shared/inputs/CheckboxInput.vue";


const __default__ = {
  name: "VocList",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const userStore = useUserStore();
const userInfo = computed(() => userStore.userInfo);

const serviceStore = useServiceStore();
const bbsStore = useBbsStore();

const listContainer = ref<HTMLElement>();
const shouldShowOnlyMyPost = ref(false);

const { vocList, totalCount, fetchVocList } = useGetVocList();

// 하루 작성 횟수 초과 모달 처리
const maxDailyVocCreationCount =
  serviceStore.serviceInfo?.maxDailyVocCreationCount;

const shouldShowDailyWritingCountLimitModal = ref(false);
const toggleShowDailyWritingCountLimitModal = (show: boolean) => {
  shouldShowDailyWritingCountLimitModal.value = show;
};

const onClickVocBtn = async () => {
  showLoadingImage();
  // 오늘 작성한 본인 글 목록을 조회
  const listResult = await bbsStore.getVocList(
    CustomCodeGroup.VOQ,
    "",
    0,
    undefined,
    true,
    true
  );
  removeLoadingImage();

  const totalCount = listResult.data?.totalCount ?? 0;
  const isExceeded = maxDailyVocCreationCount
    ? totalCount >= maxDailyVocCreationCount
    : false;

  if (isExceeded) {
    toggleShowDailyWritingCountLimitModal(true);
  } else {
    goToVoc();
  }
};

const goToVoc = () => {
  router.push({ name: RouterName.Voc });
};

const isMySecretPost = (selectedVoc: IVoc) => {
  if (!selectedVoc.secretYn) return true;

  return selectedVoc.writerUid === userInfo.value?.uid;
};

const toggleShowOnlyMyPost = (toggle: boolean) => {
  shouldShowOnlyMyPost.value = toggle;
};

const handleClickShowOnlyMyPostCheckBox = async (toggle: boolean) => {
  toggleShowOnlyMyPost(toggle);
  // 데이터 변경 시 스크롤 위치를 맨 위로 초기화
  if (listContainer.value) {
    listContainer.value.scrollTop = 0;
  }
  await fetchVocList(searchKeyword.value, shouldShowOnlyMyPost.value, true);
};

const goToVocDetail = (selectedVoc: IVoc, navigate?: () => void) => {
  if (!isMySecretPost(selectedVoc)) {
    showErrorModal("게시글 열람 불가", "비밀글은 본인만 열람 가능합니다.");
    return;
  }

  router.push({
    name: RouterName.VocDetail,
    params: { uid: selectedVoc.uid },
  });
};

const handleScroll = async () => {
  if (!listContainer.value) return;

  const nearBottom =
    listContainer.value.clientHeight + listContainer.value.scrollTop >
    listContainer.value.scrollHeight - 200;

  if (!nearBottom) return;
  if (vocList.value.length >= totalCount.value) return;
  await fetchVocList(searchKeyword.value, shouldShowOnlyMyPost.value, false);
};

// 검색
const searchKeyword = ref("");

const clearSearchKeyword = () => {
  searchKeyword.value = "";
};

const searchVoc = async () => {
  // 검색 시 스크롤 위치를 맨 위로 초기화
  if (listContainer.value) {
    listContainer.value.scrollTop = 0;
  }
  await fetchVocList(searchKeyword.value, shouldShowOnlyMyPost.value, true);
};

onMounted(() => {
  if (!listContainer.value) return;
  listContainer.value.addEventListener("scroll", handleScroll);
});

onActivated(async () => {
  showLoadingImage();
  // 문의하기 등록/수정/삭제 후 목록 화면으로 돌아왔을때 currentPage, applications 초기화 필요
  await fetchVocList(searchKeyword.value, shouldShowOnlyMyPost.value, true);
  removeLoadingImage();
});

onBeforeUnmount(() => {
  if (!listContainer.value) return;
  listContainer.value.removeEventListener("scroll", handleScroll);
});

const __returned__ = { showErrorModal, showLoadingImage, removeLoadingImage, userStore, userInfo, serviceStore, bbsStore, listContainer, shouldShowOnlyMyPost, vocList, totalCount, fetchVocList, maxDailyVocCreationCount, shouldShowDailyWritingCountLimitModal, toggleShowDailyWritingCountLimitModal, onClickVocBtn, goToVoc, isMySecretPost, toggleShowOnlyMyPost, handleClickShowOnlyMyPostCheckBox, goToVocDetail, handleScroll, searchKeyword, clearSearchKeyword, searchVoc, Page, NavigationBar, get RouterName() { return RouterName }, SearchInput, VocRow, Modal, ScrollTopControl, CheckboxInput }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})